import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { useAuth } from '@/context/AuthContext';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useI18n } from '@/i18n/I18nContext';
import { COUNTRIES } from '@/utils/countries';

const LoginPage: React.FC = () => {
	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [password, setPassword] = useState('');
	const [country, setCountry] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [code, setCode] = useState('');
	const location = useLocation();
	const redirectTo = (location.state as any)?.from || '/dashboard';
	const createAccount = (location.state as any)?.createAccount === true;

	const [isRegister, setIsRegister] = useState(createAccount);
	const [awaitingCode, setAwaitingCode] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const { signIn, register, confirmSignUp } = useAuth();
	const { t, language } = useI18n();
	const navigate = useNavigate();

	useEffect(() => {
		if (createAccount) {
			setIsRegister(true);
		}
	}, [createAccount]);

	const handleLogin = async () => {
		if (!email || !password) return;
		try {
			setSubmitting(true);
			await signIn(email, password);
			navigate(redirectTo, { replace: true });
		} catch (e: any) {
			const name = e?.name || '';
			const messagesPT: Record<string, string> = {
				"UserNotFoundException": "Usuário ou senha incorretos.",
				"NotAuthorizedException": "Usuario ou senha incorretos.",
				"UserNotConfirmedException": "Sua conta ainda não foi confirmada.",
				"CodeMismatchException": "O código informado está incorreto.",
				"ExpiredCodeException": "O código expirou.",
			};
			const messagesEN: Record<string, string> = {
				"UserNotFoundException": "Incorrect username or password.",
				"NotAuthorizedException": "Incorrect username or password.",
				"UserNotConfirmedException": "Your account has not been confirmed yet.",
				"CodeMismatchException": "The code provided is incorrect.",
				"ExpiredCodeException": "The code has expired.",
			};
			const messages = language === 'pt' ? messagesPT : messagesEN;
			const msg = messages[name] || t.common.error;

			if (name === 'UserNotConfirmedException') {
				toast.info(language === 'pt' ? 'Seu email ainda não foi confirmado.' : 'Your email has not been confirmed yet.');
			} else {
				toast.error(msg);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const handleRegister = async () => {
		if (awaitingCode) {
			if (!code) return;
			try {
				setSubmitting(true);
				await confirmSignUp(email, code);
				await signIn(email, password);
				navigate(redirectTo, { replace: true });
			} catch (e: any) {
				toast.error(e?.message || (language === 'pt' ? 'Falha ao confirmar cadastro' : 'Failed to confirm sign up'));
			} finally {
				setSubmitting(false);
			}
			return;
		}
		if (!email || !password || !name) return;
		if (!acceptedTerms) {
			toast.error(language === 'pt' ? 'Você precisa aceitar os Termos e Condições para criar uma conta.' : 'You must accept the Terms & Conditions to create an account.');
			return;
		}
		try {
			setSubmitting(true);
			// Store country as locale in Cognito
			await register(name, email, password, country || undefined);
			setAwaitingCode(true);
			toast.success(language === 'pt' ? 'Código de confirmação enviado ao seu email' : 'Confirmation code sent to your email');
		} catch (e: any) {
			toast.error(e?.message || (language === 'pt' ? 'Falha ao criar conta' : 'Failed to create account'));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				py: 4,
				px: 2,
				background: (theme) =>
					theme.palette.mode === 'dark'
						? 'linear-gradient(135deg, rgba(11, 14, 26, 1) 0%, rgba(18, 22, 36, 1) 100%)'
						: 'linear-gradient(135deg, rgba(255, 152, 0, 0.03) 0%, rgba(255, 193, 7, 0.05) 100%)'
			}}
		>
			<ToastContainer position="top-right" />
			<Container maxWidth="sm">
				<Paper
					elevation={8}
					sx={{
						p: { xs: 3, sm: 5 },
						borderRadius: 4,
						background: (theme) =>
							theme.palette.mode === 'dark'
								? 'linear-gradient(135deg, rgba(18, 22, 36, 0.95) 0%, rgba(11, 14, 26, 0.95) 100%)'
								: 'rgba(255, 255, 255, 0.98)',
						backdropFilter: 'blur(10px)',
						border: (theme) =>
							`1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
					}}
				>
					{submitting && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}
					
					{/* Header */}
					<Box sx={{ mb: 4, textAlign: 'center' }}>
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: 64,
								height: 64,
								borderRadius: '50%',
								bgcolor: 'primary.main',
								color: 'primary.contrastText',
								mb: 2,
								boxShadow: (theme) =>
									theme.palette.mode === 'dark'
										? '0 8px 24px rgba(255, 152, 0, 0.3)'
										: '0 8px 24px rgba(255, 152, 0, 0.2)'
							}}
						>
							{isRegister ? (
								<PersonAddIcon sx={{ fontSize: 32 }} />
							) : awaitingCode ? (
								<EmailIcon sx={{ fontSize: 32 }} />
							) : (
								<LoginIcon sx={{ fontSize: 32 }} />
							)}
						</Box>
						<Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
							{awaitingCode ? t.auth.confirmSignUp : isRegister ? t.auth.createAccount : t.auth.signIn}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{awaitingCode
								? language === 'pt'
									? 'Digite o código enviado ao seu email'
									: 'Enter the code sent to your email'
								: isRegister
									? t.auth.getStarted
									: t.auth.welcomeBack}
						</Typography>
					</Box>

					<form onSubmit={(e) => { e.preventDefault(); awaitingCode ? handleRegister() : isRegister ? handleRegister() : handleLogin(); }}>
						<Stack spacing={3}>
							{isRegister && !awaitingCode && (
								<TextField
									fullWidth
									label={t.auth.name}
									placeholder={t.auth.name}
									value={name}
									onChange={(e) => setName(e.target.value)}
									disabled={submitting}
									required
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PersonIcon color="action" />
											</InputAdornment>
										)
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2
										}
									}}
								/>
							)}
							
							{isRegister && !awaitingCode && (
								<TextField
									select
									fullWidth
									label={t.auth.country}
									value={country}
									onChange={(e) => setCountry(e.target.value)}
									disabled={submitting}
									required
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PublicIcon color="action" />
											</InputAdornment>
										)
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2
										}
									}}
								>
									<MenuItem value="">
										<em>{t.auth.selectCountry}</em>
									</MenuItem>
									{COUNTRIES.map((c) => (
										<MenuItem key={c.code} value={c.code}>
											{c.name}
										</MenuItem>
									))}
								</TextField>
							)}

							<TextField
								fullWidth
								type="email"
								label={t.auth.email}
								placeholder={t.auth.emailPlaceholder}
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								disabled={submitting}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<EmailIcon color="action" />
										</InputAdornment>
									)
								}}
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: 2
									}
								}}
							/>
							
							{isRegister && !awaitingCode && (
								<FormControlLabel
									control={
										<Checkbox
											checked={acceptedTerms}
											onChange={(e) => setAcceptedTerms(e.target.checked)}
											disabled={submitting}
											color="primary"
											sx={{ py: 0 }}
										/>
									}
									label={
										<Typography variant="body2" component="span" sx={{ lineHeight: 1.5 }}>
											{t.auth.acceptTerms}{' '}
											<Link component={RouterLink} to="/terms" target="_blank" rel="noopener" underline="hover" color="primary">
												({language === 'pt' ? 'Termos e Condições' : 'Terms & Conditions'})
											</Link>
										</Typography>
									}
									sx={{ alignItems: 'center', mr: 0 }}
								/>
							)}

							{!awaitingCode && (
								<TextField
									fullWidth
									type={showPassword ? 'text' : 'password'}
									label={t.auth.password}
									placeholder={t.auth.passwordPlaceholder}
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									disabled={submitting}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<LockIcon color="action" />
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label={showPassword ? (language === 'pt' ? 'Ocultar senha' : 'Hide password') : (language === 'pt' ? 'Mostrar senha' : 'Show password')}
													onClick={() => setShowPassword((prev) => !prev)}
													onMouseDown={(e) => e.preventDefault()}
													edge="end"
													disabled={submitting}
													size="small"
												>
													{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
												</IconButton>
											</InputAdornment>
										)
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2
										}
									}}
								/>
							)}
							
							{awaitingCode && (
								<TextField
									fullWidth
									label={t.auth.confirmCode}
									placeholder={t.auth.codePlaceholder}
									required
									value={code}
									onChange={(e) => setCode(e.target.value)}
									disabled={submitting}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<EmailIcon color="action" />
											</InputAdornment>
										)
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2
										}
									}}
								/>
							)}

							{awaitingCode ? (
								<Button
									variant="contained"
									size="large"
									fullWidth
									onClick={handleRegister}
									disabled={submitting || !code}
									sx={{
										py: 1.5,
										borderRadius: 2,
										fontWeight: 600,
										textTransform: 'none',
										fontSize: '1rem',
										boxShadow: (theme) =>
											theme.palette.mode === 'dark'
												? '0 4px 12px rgba(255, 152, 0, 0.3)'
												: '0 4px 12px rgba(255, 152, 0, 0.25)'
									}}
								>
									{submitting ? (
										<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
											<CircularProgress color="inherit" size={20} />
											<Typography component="span" variant="button">{t.auth.confirming}</Typography>
										</Stack>
									) : (
										t.common.confirm
									)}
								</Button>
							) : isRegister ? (
								<>
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={handleRegister}
										disabled={submitting || !email || !password || !name || !country || !acceptedTerms}
										sx={{
											py: 1.5,
											borderRadius: 2,
											fontWeight: 600,
											textTransform: 'none',
											fontSize: '1rem',
											boxShadow: (theme) =>
												theme.palette.mode === 'dark'
													? '0 4px 12px rgba(255, 152, 0, 0.3)'
													: '0 4px 12px rgba(255, 152, 0, 0.25)'
										}}
									>
										{submitting ? (
											<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
												<CircularProgress color="inherit" size={20} />
												<Typography component="span" variant="button">{t.auth.creating}</Typography>
											</Stack>
										) : (
											t.common.create
										)}
									</Button>
									<Divider sx={{ my: 1 }}>
										<Typography variant="caption" color="text.secondary">
											{t.auth.alreadyHaveAccount}
										</Typography>
									</Divider>
									<Button
										variant="outlined"
										size="large"
										fullWidth
										onClick={() => setIsRegister(false)}
										disabled={submitting}
										sx={{
											py: 1.5,
											borderRadius: 2,
											fontWeight: 600,
											textTransform: 'none',
											fontSize: '1rem'
										}}
									>
										{t.auth.backToLogin}
									</Button>
								</>
							) : (
								<>
									<Button
										variant="contained"
										size="large"
										fullWidth
										onClick={handleLogin}
										disabled={submitting || !email || !password}
										sx={{
											py: 1.5,
											borderRadius: 2,
											fontWeight: 600,
											textTransform: 'none',
											fontSize: '1rem',
											boxShadow: (theme) =>
												theme.palette.mode === 'dark'
													? '0 4px 12px rgba(255, 152, 0, 0.3)'
													: '0 4px 12px rgba(255, 152, 0, 0.25)'
										}}
									>
										{submitting ? (
											<Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
												<CircularProgress color="inherit" size={20} />
												<Typography component="span" variant="button">{t.auth.entering}</Typography>
											</Stack>
										) : (
											t.auth.signIn
										)}
									</Button>
									<Divider sx={{ my: 1 }}>
										<Typography variant="caption" color="text.secondary">
											{t.auth.dontHaveAccount}
										</Typography>
									</Divider>
									<Button
										variant="outlined"
										size="large"
										fullWidth
										onClick={() => setIsRegister(true)}
										disabled={submitting}
										sx={{
											py: 1.5,
											borderRadius: 2,
											fontWeight: 600,
											textTransform: 'none',
											fontSize: '1rem'
										}}
									>
										{t.auth.createAccount}
									</Button>
								</>
							)}
							
							{!awaitingCode && !isRegister && (
								<Box sx={{ textAlign: 'center', mt: 1 }}>
									<Link
										underline="hover"
										onClick={() => navigate('/recuperar-senha')}
										sx={{
											cursor: 'pointer',
											color: 'text.secondary',
											fontSize: '0.875rem',
											'&:hover': {
												color: 'primary.main'
											}
										}}
									>
										{t.auth.forgotPassword}
									</Link>
								</Box>
							)}
						</Stack>
					</form>
				</Paper>
			</Container>
		</Box>
	);
};

export default LoginPage;
